// Processing page — shows every record approved but not yet imported.
// Covers the three previously-invisible states so techs can see what's queued
// for the bot, what the bot is actively entering, and what failed.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../lib/api";
import type { ProcessingRow, TargetPms } from "@shared/types";
import { TARGET_PMS_LABEL } from "@shared/types";

const STORAGE_STORE_KEY = "smartrx-entry.currentStoreCode";

function getCurrentStoreCode(): string {
  return (typeof window !== "undefined" && window.localStorage.getItem(STORAGE_STORE_KEY)) || "GV";
}

// Generic labels — never say "PK" since we support PK / PioneerRx / ReComRx now.
const STATUS_LABEL: Record<ProcessingRow["status"], string> = {
  ready_for_pk:   "Waiting for bot",
  pk_filing:      "Bot filing",
  pk_error_review: "Bot error — needs review",
};
const STATUS_CLASS: Record<ProcessingRow["status"], string> = {
  ready_for_pk:   "bg-pill text-ink-soft",
  pk_filing:      "bg-accent-soft text-accent",
  pk_error_review: "bg-bad-soft text-bad",
};

export function Processing() {
  const [rows, setRows] = useState<ProcessingRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const storeCode = getCurrentStoreCode();
  const timerRef = useRef<number | null>(null);

  function reload(ctrl?: AbortController) {
    setLoading(true);
    apiGet<ProcessingRow[]>(`/processing?store=${storeCode}`, ctrl?.signal)
      .then((data) => { setRows(data); setError(null); })
      .catch((e) => { if (!ctrl?.signal.aborted) { setRows([]); setError(String(e)); } })
      .finally(() => { if (!ctrl?.signal.aborted) setLoading(false); });
  }

  // Auto-refresh every 15s so techs can watch records move between states.
  useEffect(() => {
    const ctrl = new AbortController();
    reload(ctrl);
    timerRef.current = window.setInterval(() => reload(ctrl), 15000);
    return () => {
      ctrl.abort();
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [storeCode]);

  const waiting   = rows.filter(r => r.status === "ready_for_pk").length;
  const filing    = rows.filter(r => r.status === "pk_filing").length;
  const errored   = rows.filter(r => r.status === "pk_error_review").length;

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-2xl font-semibold">Processing</h2>
        <span className="text-ink-soft text-sm">{storeCode}</span>
      </div>
      <p className="text-ink-soft mb-6">
        {error
          ? <span className="text-bad">Processing unavailable: {error}</span>
          : <>Approved records in flight. Auto-refreshes every 15s.{" "}
             {filing > 0 && <span>Bot is filing <strong>{filing}</strong>.</span>}{" "}
             {waiting > 0 && <span><strong>{waiting}</strong> waiting.</span>}{" "}
             {errored > 0 && <span className="text-bad"><strong>{errored}</strong> errored.</span>}
            </>}
      </p>

      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-[64px_1.1fr_1.1fr_160px_100px_1fr] gap-3 px-4 py-2 text-xs uppercase tracking-wide text-ink-soft border-b border-border bg-pill">
          <span>#</span>
          <span>Patient</span>
          <span>Drug</span>
          <span>State</span>
          <span>PMS</span>
          <span>Error / Updated</span>
        </div>

        {loading && rows.length === 0 && (
          <p className="text-ink-soft text-sm py-6 text-center">Loading…</p>
        )}
        {!loading && rows.length === 0 && !error && (
          <p className="text-ink-soft text-sm py-10 text-center">
            Nothing processing right now. Approve something in the queue to see it land here.
          </p>
        )}

        <div className="divide-y divide-border">
          {rows.map((r) => (
            <Link
              key={r.rxId}
              to={`/review/${r.rxId}`}
              className="grid grid-cols-[64px_1.1fr_1.1fr_160px_100px_1fr] gap-3 px-4 py-2.5 items-center text-sm hover:bg-pill"
            >
              <span className="text-ink-soft">#{r.rxId}</span>
              <span className="truncate"><strong>{r.patientName ?? "—"}</strong></span>
              <span className="truncate">{r.drug}</span>
              <span>
                <span className={`text-[11px] px-1.5 py-0.5 rounded ${STATUS_CLASS[r.status]}`}>
                  {STATUS_LABEL[r.status]}
                </span>
              </span>
              <span>
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-accent-soft text-accent">
                  {TARGET_PMS_LABEL[r.targetPms as TargetPms] ?? r.targetPms}
                </span>
              </span>
              <span className="truncate text-ink-soft text-xs" title={r.statusUpdatedAt}>
                {r.status === "pk_error_review" && r.errorStep
                  ? <span className="text-bad">{r.errorStep}{r.errorDialog ? ` — ${r.errorDialog.slice(0, 60)}` : ""}</span>
                  : formatRelative(r.statusUpdatedAt)}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return d.toLocaleString(undefined, { month: "short", day: "numeric" });
}
